import type {
    BundleItem,
    BundleItemOption,
    BundleProduct,
    CurrencyEnum
} from '@pylot-data/pylotschema'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { Select, MultiSelect, Radio, Checkbox } from './type'
import {
    SelectedBundleProductsAllTypes,
    SelectedBundleProducts,
    SelectedBundleProductsValue,
    BundleTypeProps,
    SelectedLabels
} from './type/type.d'
import { useState } from 'react'
import { ProductStockStatus } from '@pylot-data/hooks/product/use-product-ui'
import { MediaGalleryInterface, Maybe } from '@pylot-data/fwrdschema'
import { useRouter } from 'next/router'
import { PriceTypeEnum } from '@pylot-data/pylotschema.d'

type BundleProductSelectorProps = {
    bundleProduct: BundleProduct
    selectedBundleProducts: SelectedBundleProductsAllTypes
    setSelectedBundleProducts: (
        SelectedBundleProducts: SelectedBundleProductsAllTypes,
        selectedGallery?: Maybe<Maybe<MediaGalleryInterface>[]>
    ) => void
    setSelectedBundlePrice: Dispatch<
        SetStateAction<{ price: number; discountPrice: number } | null>
    >
    showAllSelected?: boolean
}

const renderType: {
    [key: string]: React.FC<BundleTypeProps>
} = {
    ['checkbox']: Checkbox,
    ['radio']: Radio,
    ['multi']: MultiSelect,
    ['select']: Select
}

export const BundleProductSelector = ({
    bundleProduct,
    selectedBundleProducts,
    setSelectedBundleProducts,
    setSelectedBundlePrice,
    showAllSelected
}: BundleProductSelectorProps): JSX.Element => {
    const bundleItemArray = bundleProduct.items as BundleItem[]
    const [selectedLabels, setSelectedLabels] = useState<SelectedLabels>({})
    const { dynamic_price: dynamicPrice } = bundleProduct
    const { locale = 'en' } = useRouter()
    const mainFinalPrice = bundleProduct?.bundle_main_price?.price

    useEffect(() => {
        let totalBundlePrice = null
        const mainDiscountPercent = bundleProduct.bundle_main_price.discount

        if (dynamicPrice) {
            totalBundlePrice = bundleItemArray.reduce(
                (totalPrice: number, BundleItem: BundleItem) => {
                    const selectedUids: SelectedBundleProducts[] | string =
                        selectedBundleProducts[BundleItem?.uid as string]?.uid
                    if (selectedUids) {
                        if (typeof selectedUids === 'string') {
                            const selectedOption = BundleItem?.options?.find(
                                (i) => selectedUids == i?.uid
                            )
                            totalPrice +=
                                (selectedOption?.product?.price_range
                                    ?.minimum_price?.final_price.value || 0) *
                                (selectedBundleProducts[
                                    BundleItem?.uid as string
                                ].value || 1)
                        } else {
                            totalPrice +=
                                BundleItem?.options?.reduce(
                                    (totalOptionPrice: number, option) => {
                                        const selectedUid = selectedUids.find(
                                            (selectedUid) =>
                                                selectedUid.multiSelectUid ===
                                                option?.uid
                                        )
                                        if (selectedUid) {
                                            totalOptionPrice +=
                                                (option?.product?.price_range
                                                    ?.minimum_price?.final_price
                                                    .value || 0) *
                                                (selectedUid.value || 1)
                                        }
                                        return totalOptionPrice
                                    },
                                    0
                                ) || 0
                        }
                    }
                    return totalPrice
                },
                0
            )
        } else {
            // For non dynamic price
            let isAdded = false // Check product is added.

            const getTotalPriceByType = (
                totalPrice: number,
                uid: string,
                option: BundleItemOption
            ) => {
                if (option?.price_type === PriceTypeEnum.Fixed) {
                    // If non dynamic individual price is fixed.
                    totalPrice +=
                        (option?.price || 0) *
                        (selectedBundleProducts[uid].value || 1)
                } else {
                    // Get price from percent.
                    const regularPriceInFixed =
                        mainFinalPrice * (option?.price! / 100)
                    totalPrice +=
                        regularPriceInFixed *
                        (selectedBundleProducts[uid].value || 1)
                }
                return totalPrice
            }

            totalBundlePrice =
                bundleItemArray.reduce(
                    (totalPrice: number, BundleItem: BundleItem) => {
                        const selectedUids: SelectedBundleProducts[] | string =
                            selectedBundleProducts[BundleItem?.uid as string]
                                ?.uid
                        if (selectedUids) {
                            if (typeof selectedUids === 'string') {
                                // get total price for dropdown and radio button.
                                const selectedOption = BundleItem?.options?.find(
                                    (i) => selectedUids == i?.uid
                                )
                                isAdded = true
                                totalPrice = getTotalPriceByType(
                                    totalPrice,
                                    BundleItem.uid as string,
                                    selectedOption as BundleItemOption
                                )
                            } else {
                                // get total price for multi select and checkbox.
                                totalPrice +=
                                    BundleItem?.options?.reduce(
                                        (totalOptionPrice: number, option) => {
                                            const selectedUid = selectedUids.find(
                                                (selectedUid) =>
                                                    selectedUid.multiSelectUid ===
                                                    option?.uid
                                            )
                                            if (selectedUid) {
                                                isAdded = true
                                                totalOptionPrice = getTotalPriceByType(
                                                    totalOptionPrice,
                                                    BundleItem.uid as string,
                                                    option as BundleItemOption
                                                )
                                            }
                                            return totalOptionPrice
                                        },
                                        0
                                    ) || 0
                            }
                        }
                        return totalPrice
                    },
                    0
                ) || 0

            if (isAdded) {
                totalBundlePrice += mainFinalPrice
            }
        }
        setSelectedBundlePrice({
            discountPrice: mainDiscountPercent
                ? totalBundlePrice * (mainDiscountPercent / 100)
                : totalBundlePrice,
            price: totalBundlePrice
        })
    }, [selectedBundleProducts])

    useEffect(() => {
        // set default value on intial load.
        const defaultBundle: SelectedBundleProductsAllTypes = {}
        let defaultGallery:
            | Maybe<Maybe<MediaGalleryInterface>[]>
            | undefined = []
        const defaultLabels: SelectedLabels = {}
        bundleItemArray.forEach((bundleItem) => {
            if (bundleItem.type === 'select' || bundleItem.type === 'radio') {
                const defaultSelectOrRadio = bundleItem?.options?.find(
                    (i) =>
                        i?.is_default &&
                        i?.product?.stock_status === ProductStockStatus.InStock
                )
                if (defaultSelectOrRadio) {
                    if (bundleItemArray.length === 1) {
                        defaultGallery =
                            defaultSelectOrRadio?.product?.media_gallery
                    }
                    defaultBundle[bundleItem?.uid as string] = {
                        uid: defaultSelectOrRadio.uid,
                        value: defaultSelectOrRadio.quantity as number
                    }
                    defaultLabels[bundleItem?.uid as string] = [
                        defaultSelectOrRadio?.label || ''
                    ]
                }
            } else {
                bundleItem?.options?.forEach((option) => {
                    if (
                        option?.is_default &&
                        option?.product?.stock_status ===
                            ProductStockStatus.InStock
                    ) {
                        if (defaultBundle[bundleItem?.uid as string]) {
                            ;(defaultBundle[bundleItem?.uid as string].uid as [

                            ]).push({
                                multiSelectUid: option.uid,
                                value: option.quantity
                            } as never)
                            defaultLabels[bundleItem?.uid as string] = [
                                ...(defaultLabels[bundleItem?.uid as string] ??
                                    []),
                                option?.label as string
                            ]
                        } else {
                            defaultBundle[bundleItem?.uid as string] = {
                                uid: [
                                    {
                                        multiSelectUid: option.uid,
                                        value: option.quantity as number
                                    }
                                ]
                            }
                            defaultLabels[bundleItem?.uid as string] = [
                                option?.label as string
                            ]
                        }
                    }
                })
            }
        })
        if (defaultGallery.length) {
            setSelectedBundleProducts(defaultBundle, defaultGallery)
        } else {
            setSelectedBundleProducts(defaultBundle)
        }
        setSelectedLabels(defaultLabels)
    }, [bundleProduct])

    const renderSelectedOptionsWithQty = (): JSX.Element | null => {
        const allOptions = bundleItemArray?.reduce(
            (allItems: BundleItemOption[], items) => [
                ...allItems,
                ...((items.options as BundleItemOption[]) || [])
            ],
            [] as BundleItemOption[]
        )

        return (
            <div>
                {Object.values(selectedBundleProducts).reduce(
                    (
                        selectedOptions: JSX.Element[],
                        i: SelectedBundleProductsValue
                    ) => {
                        if (typeof i.uid === 'string') {
                            const selected = allOptions.find(
                                (option) => i?.uid === option?.uid
                            )
                            if (selected) {
                                selectedOptions = [
                                    ...selectedOptions,
                                    <span
                                        key={`label-${selected.uid}`}
                                    >{`${selected?.label} X ${i.value}`}</span>
                                ]
                            }
                        } else {
                            const multiSelectedOptions =
                                i.uid.reduce(
                                    (
                                        selectedOptionsForMulti: JSX.Element[],
                                        item: SelectedBundleProducts
                                    ) => {
                                        const selected = allOptions.find(
                                            (option) =>
                                                item?.multiSelectUid ===
                                                option?.uid
                                        )
                                        if (selected) {
                                            selectedOptionsForMulti = [
                                                ...selectedOptionsForMulti,
                                                <span
                                                    key={`label-${selected.uid}`}
                                                >{`${selected?.label} X ${item.value}`}</span>
                                            ]
                                        }
                                        return selectedOptionsForMulti
                                    },
                                    []
                                ) || []
                            selectedOptions = [
                                ...selectedOptions,
                                ...multiSelectedOptions
                            ]
                        }
                        return selectedOptions
                    },
                    []
                )}
            </div>
        )
    }

    return (
        <div>
            {showAllSelected && renderSelectedOptionsWithQty()}
            {bundleItemArray.map((bundleItem) => {
                const { title, required, type, uid } = bundleItem
                const BundleOptionType = renderType[type as string]
                const labels = selectedLabels[bundleItem?.uid as string]
                return (
                    <div className="pb-4" key={`type-${uid}`}>
                        <div className="pdp-swatch-label-wrap">
                            <label className="pdp-swatch-label">{title}</label>
                            {required && (
                                <strong className="asterisk"> *</strong>
                            )}
                            <div className="pl-1">
                                {labels?.map((label, i) => {
                                    if (label) {
                                        return (
                                            <span
                                                className="flex flex-col md:inline"
                                                key={`${label}-${i}`}
                                            >
                                                {label +
                                                    (labels.length - 1 !== i
                                                        ? ', '
                                                        : '')}
                                            </span>
                                        )
                                    }
                                    return null
                                })}
                            </div>
                        </div>

                        <BundleOptionType
                            setSelectedBundleProducts={
                                setSelectedBundleProducts
                            }
                            bundleItem={bundleItem}
                            selectedBundleProducts={selectedBundleProducts}
                            setSelectedLabels={setSelectedLabels}
                            selectedLabels={selectedLabels}
                            dynamicPrice={dynamicPrice as boolean}
                            currency={
                                bundleProduct.price_range.minimum_price
                                    .final_price.currency as CurrencyEnum
                            }
                            locale={locale}
                            mainFinalPrice={mainFinalPrice}
                            useFlavour
                        />
                    </div>
                )
            })}
        </div>
    )
}
